import Logo from "@/shared/components/logo/logo";
import "./invite-page.scss";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { MessageCircleQuestion, Timer } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import image1 from "/images/image1.png";
import image2 from "/images/image2.png";
import cursor from "/images/cursor.png";

import { motion } from "framer-motion";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { useState, useEffect } from "react";

import { getDashboardLink } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";
import { useSelector } from "@/core/redux/store";

import { inviteService } from "@/shared/services/api/invite";
import {
  IInviteDetailResponse,
  IInviteListResponse,
} from "@/shared/services/interface/responses/invite-response";
import CustomToast from "@/shared/hooks/custom-toast";

const FloatingBadge = ({ name = "Adsız Şartname" }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setPosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <motion.div
      className="fixed bg-[#1BC47D] px-[4px]"
      style={{
        top: (position.y * 8) / 100,
        left: (position.x * 4) / 100,
        boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.16)",
      }}
      animate={{
        x: position.x,
        y: position.y,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
      }}
    >
      <img
        src={cursor}
        alt="cursor"
        className="w-[25px] h-[25px] absolute -top-[22px] -left-[22px]"
      />
      <span className="text-white text-[11px] font-600">{name}</span>
    </motion.div>
  );
};

const user = [
  {
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: image1,
  },
  {
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: image2,
  },
  {
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: image1,
  },
];

export default function InvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shareLinkHash = searchParams.get("sh");
  const [specificationID, setSpecificationID] =
    useState<IInviteDetailResponse | null>(null);
  const specificationDetail = useSelector(
    (state) => state.specification.specification.specification
  );

  const pathname = useLocation().pathname;

  const { error } = CustomToast();

  const getDetail = async () => {
    if (!shareLinkHash) return;

    const response = await inviteService.getInviteDetail(shareLinkHash);
    if (
      response.success &&
      response.data &&
      response.data.id &&
      response.data.specificationId
    ) {
      setSpecificationID(response.data);
      const responseInviteList = await inviteService.getInviteList(
        response.data.specificationId
      );
      if (responseInviteList.success && responseInviteList.data) {
        const user = localStorage.getItem("user");
        const parsedUser = JSON.parse(user || "{}");
        const existingUser = responseInviteList.data.find(
          (item: IInviteListResponse) => item.email === parsedUser.email
        );

        if (!existingUser) {
          error({
            title: t("you-dont-have-permission-to-access-this-specification"),
            position: "bottom-right",
          });
          navigate("/404");
        }
      }
    }
  };

  const checkSetting = async () => {
    const sh = searchParams.get("sh");
    const redirectUrl = `${pathname}?sh=${sh}`;
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      error({
        title: t("please-login-to-continue"),
        position: "bottom-right",
      });

      setTimeout(() => {
        navigate(
          `/${ROUTES.Auth.Root}/${
            ROUTES.Auth.SignUp
          }?redirectUrl=${redirectUrl.replace("/", "")}`
        );
      }, 1000);
      return;
    }

    getDetail();
  };

  const acceptInvite = async () => {
    if (!specificationID?.hash && !specificationID?.specificationId) return;

    const response = await inviteService.acceptInvite(specificationID.hash);
    if (response.success) {
      navigate(
        getDashboardLink(ROUTES.Dashboard.Specification.Create) +
          `?specificationId=${specificationID.specificationId}`
      );
    }
  };

  useEffect(() => {
    checkSetting();
  }, [shareLinkHash]);

  return (
    <div className="share-page w-full h-full ">
      <div className="header flex items-center justify-center">
        <Logo
          style={{ height: "30px" }}
          onClick={() => {
            navigate("/");
          }}
        />
      </div>
      <div className="content flex items-center justify-center">
        <FloatingBadge
          name={specificationDetail?.name || t("namelessSpecification")}
        />
        <div className="share-content flex flex-col items-center justify-center  rounded-lg">
          <div className="share-content-header flex flex-col items-center justify-center">
            <div className="header-users flex items-center justify-center px-[4px] py-[6px] border rounded-full max-w-max">
              <div className="flex items-center mr-3.5 relative h-[24px] ">
                {user.map((item, index) => (
                  <Avatar
                    className={`absolute  w-[26px] h-[26px] mr-[-8px] border-[2.5px] border-white`}
                    style={{
                      left: `${index * 15}px`,
                      zIndex: `${user.length * 5 - index}`,
                    }}
                    key={index}
                  >
                    <AvatarImage
                      src={item.avatar || ""}
                      className={`w-[24px] h-[24px]`}
                    />
                    <AvatarFallback>{item.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <p className="text-sm font-700 text-[#31373D] ml-[45px]">
                {t("procright-is-waiting-for-you")}
              </p>
            </div>
            <h1 className="share-title  font-700 ">
              {t("invite-page-title").replace(
                "{owner}",
                specificationDetail.company.name
              )}
            </h1>
          </div>

          <p
            className="share-description-text text-base font-500"
            dangerouslySetInnerHTML={{
              __html: t("invite-page-description"),
            }}
          />

          <Button
            variant="default"
            className="start-now-button bg-green-700 font-600 text-sm h-[45px]"
            onClick={acceptInvite}
          >
            {t("login")}
          </Button>

          <p className="footer-title text-base font-700 text-black">
            {t("how-long-will-it-take")}
          </p>
          <div className="footer-buttons flex items-center justify-center">
            <Button variant="outline" className="footer-button">
              <MessageCircleQuestion size={20} />
              {t("have-count-items", {
                count:
                  specificationID?.specification.specificationItemsCount || 0,
              })}
            </Button>
            <Button variant="outline" className="footer-button">
              <Timer size={20} />
              {t("minute-count", {
                minute: specificationID?.specification.specificationItemsCount
                  ? String(
                      Math.floor(
                        (specificationID?.specification
                          .specificationItemsCount *
                          8) /
                          60
                      )
                    )
                  : "10",
              })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
